/**
 * SPLINE math helpers — NURBS evaluation, knot vectors, fit-points
 * interpolation, and cubic Bezier conversion.
 *
 * AutoCAD stores SPLINE entities either as:
 *   - **Fit-points mode**: user-specified points the curve must pass
 *     through. Interior control points are computed via global cubic
 *     interpolation (open-uniform knot parameters). Optional start/end
 *     tangent vectors constrain the derivatives at the endpoints.
 *   - **Control-points (NURBS) mode**: user specifies degree, knot
 *     vector, control points, and (optionally) per-control-point
 *     weights. The curve approximates the control polygon.
 *
 * Cubic spline (degree=3) is the AutoCAD default; the helpers below
 * support arbitrary degree but the typed entity always assumes degree
 * is well-formed (knot length = controlPoints + degree + 1).
 *
 * The renderer consumes `splineToCubicBezier` output and emits SVG path
 * cubic-Bezier `C` commands. Per CLAUDE.md ПРИОРИТЕТ #1:
 *   - cubic Bezier ONLY (never quadratic, never polylinear approx)
 *   - tangent vectors used in fit-points mode (full fidelity)
 *   - rational NURBS supported (weights honoured)
 */

export type SplinePoint = { x: number; y: number; z?: number };

export type CubicBezierSegment = {
  p0: SplinePoint;
  cp1: SplinePoint;
  cp2: SplinePoint;
  p3: SplinePoint;
};

/**
 * Generate a clamped uniform knot vector for the given control-point
 * count and degree. Length = numControlPoints + degree + 1. Used for
 * fit-points mode and for control-points mode when the user does not
 * supply an explicit knot vector.
 *
 * Clamped uniform knots: degree+1 zeros at the start, degree+1 ones at
 * the end, uniform spacing through the interior. This makes the curve
 * pass exactly through the first and last control points (typical
 * AutoCAD behaviour for open splines).
 */
export function uniformKnotVector(
  numControlPoints: number,
  degree: number,
): number[] {
  if (numControlPoints < degree + 1) {
    // Degenerate input — return a minimal valid clamp so callers do not
    // crash. The downstream NURBS evaluator will produce a single
    // control point (or a degenerate curve) but no NaNs.
    const total = Math.max(numControlPoints + degree + 1, 2);
    const knots: number[] = [];
    for (let i = 0; i < total; i++) knots.push(i < total / 2 ? 0 : 1);
    return knots;
  }

  const knots: number[] = [];
  // Start clamp: degree+1 zeros
  for (let i = 0; i <= degree; i++) knots.push(0);
  // Interior knots: uniform between 0 and 1
  const interior = numControlPoints - degree - 1;
  for (let i = 1; i <= interior; i++) knots.push(i / (interior + 1));
  // End clamp: degree+1 ones
  for (let i = 0; i <= degree; i++) knots.push(1);
  return knots;
}

/**
 * Cox-de Boor recursion for the i-th B-spline basis function of degree
 * `p` evaluated at parameter `u` against the given knot vector.
 *
 * Standard recurrence:
 *   N_{i,0}(u) = 1 if knots[i] <= u < knots[i+1] else 0
 *   N_{i,p}(u) = ((u - knots[i]) / (knots[i+p] - knots[i])) * N_{i,p-1}(u)
 *              + ((knots[i+p+1] - u) / (knots[i+p+1] - knots[i+1])) * N_{i+1,p-1}(u)
 *
 * Zero-denominator terms drop to 0 per de Boor's convention. The
 * special-case at u == knotVector[end] ensures the final point is
 * included (basis evaluates to 1 there).
 */
export function basisFunction(
  i: number,
  p: number,
  u: number,
  knots: number[],
): number {
  const lastKnot = knots[knots.length - 1] ?? 0;
  const ki = knots[i] ?? 0;
  const ki1 = knots[i + 1] ?? 0;
  const kip = knots[i + p] ?? 0;
  const kip1 = knots[i + p + 1] ?? 0;

  if (p === 0) {
    if (u >= ki && u < ki1) return 1;
    // Special-case the rightmost point so the curve closes onto the
    // final control point. When the parameter is exactly at the end of
    // the knot vector and this is the last non-zero span, return 1.
    if (Math.abs(u - lastKnot) < 1e-9 && ki <= u && u <= ki1 && ki < ki1) {
      return 1;
    }
    return 0;
  }

  let left = 0;
  const denomLeft = kip - ki;
  if (Math.abs(denomLeft) > 1e-12) {
    left = ((u - ki) / denomLeft) * basisFunction(i, p - 1, u, knots);
  }

  let right = 0;
  const denomRight = kip1 - ki1;
  if (Math.abs(denomRight) > 1e-12) {
    right = ((kip1 - u) / denomRight) * basisFunction(i + 1, p - 1, u, knots);
  }

  return left + right;
}

function normalizeWeightedNurbsPoint(
  controlPoints: SplinePoint[],
  sumX: number,
  sumY: number,
  sumZ: number,
  sumW: number,
): SplinePoint {
  if (Math.abs(sumW) < 1e-12) {
    return {
      x: controlPoints[0]?.x ?? 0,
      y: controlPoints[0]?.y ?? 0,
      z: controlPoints[0]?.z ?? 0,
    };
  }
  return { x: sumX / sumW, y: sumY / sumW, z: sumZ / sumW };
}

function weightedNurbsPoint(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  u: number,
  firstIndex = 0,
  basisValues?: number[],
): SplinePoint {
  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;
  let sumW = 0;
  const contributionCount = basisValues?.length ?? controlPoints.length;
  for (let offset = 0; offset < contributionCount; offset++) {
    const index = firstIndex + offset;
    const controlPoint = controlPoints[index];
    if (controlPoint === undefined) continue;
    const basis =
      basisValues?.[offset] ?? basisFunction(index, degree, u, knots);
    if (basis === 0) continue;
    const weightedBasis = (weights?.[index] ?? 1) * basis;
    sumX += controlPoint.x * weightedBasis;
    sumY += controlPoint.y * weightedBasis;
    sumZ += (controlPoint.z ?? 0) * weightedBasis;
    sumW += weightedBasis;
  }
  return normalizeWeightedNurbsPoint(controlPoints, sumX, sumY, sumZ, sumW);
}

/**
 * Evaluate a NURBS curve at parameter `u`. When all weights are 1 the
 * result is identical to a non-rational B-spline. Returns the curve
 * point in the same coordinate space as the control points.
 *
 * Formula:
 *   C(u) = sum_i [ w_i * P_i * N_{i,p}(u) ] / sum_i [ w_i * N_{i,p}(u) ]
 */
export function evaluateNurbs(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  u: number,
): SplinePoint {
  // This evaluator is called once per sample in rendering/math hot paths.
  // The shared accumulator derives each basis term in place when no compact
  // span basis is supplied, so this path stages no per-control-point objects.
  return weightedNurbsPoint(controlPoints, knots, weights, degree, u);
}

/**
 * Evaluate only the degree+1 non-zero basis functions for a previously
 * resolved knot span. This is algebraically identical to `evaluateNurbs`, but
 * avoids a full control-point scan in repeated many-span searches.
 */
export function evaluateNurbsAtSpan(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  u: number,
  span: number,
): SplinePoint {
  const basis = Array.from({ length: degree + 1 }, () => 0);
  const left = Array.from({ length: degree + 1 }, () => 0);
  const right = Array.from({ length: degree + 1 }, () => 0);
  basis[0] = 1;
  for (let j = 1; j <= degree; j++) {
    left[j] = u - (knots[span + 1 - j] ?? 0);
    right[j] = (knots[span + j] ?? 0) - u;
    let saved = 0;
    for (let r = 0; r < j; r++) {
      const denominator = right[r + 1]! + left[j - r]!;
      const term = Math.abs(denominator) > 1e-12 ? basis[r]! / denominator : 0;
      basis[r] = saved + right[r + 1]! * term;
      saved = left[j - r]! * term;
    }
    basis[j] = saved;
  }
  return weightedNurbsPoint(
    controlPoints,
    knots,
    weights,
    degree,
    u,
    span - degree,
    basis,
  );
}

/**
 * Sample `numSamples` points uniformly along the spline parameter
 * domain [knots[degree], knots[knots.length - degree - 1]]. Used by
 * fallback rendering and for fit-points mode reconstruction.
 */
export function sampleNurbs(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  numSamples: number,
): SplinePoint[] {
  if (controlPoints.length === 0 || numSamples < 2)
    return controlPoints.slice();
  const uMin = knots[degree] ?? 0;
  const uMax = knots[knots.length - degree - 1] ?? 1;
  const samples: SplinePoint[] = [];
  for (let i = 0; i < numSamples; i++) {
    const t = i / (numSamples - 1);
    const u = uMin + t * (uMax - uMin);
    samples.push(evaluateNurbs(controlPoints, knots, weights, degree, u));
  }
  return samples;
}

/**
 * Compute control points for a cubic spline that interpolates the given
 * fit points. Optional start / end tangent vectors override the natural
 * spline boundary conditions (per AutoCAD's SPLINE FIT mode).
 *
 * Algorithm (centripetal parametrisation + tridiagonal solve):
 *   1. Compute parameter t_i for each fit point using chord-length
 *      parametrisation.
 *   2. Solve the tridiagonal linear system M * P = R where M encodes
 *      the cubic-spline continuity constraints and R encodes fit
 *      points + tangent constraints.
 *
 * Returns numFitPoints + 2 control points (start & end tangent
 * helpers) and a clamped knot vector matching the parametrisation.
 */
export function fitPointsToControlPoints(
  fitPoints: SplinePoint[],
  startTangent: SplinePoint | undefined,
  endTangent: SplinePoint | undefined,
  degree: number = 3,
): { controlPoints: SplinePoint[]; knots: number[] } {
  const n = fitPoints.length;
  if (n < 2) {
    return {
      controlPoints: fitPoints.slice(),
      knots: uniformKnotVector(Math.max(n, 1), degree),
    };
  }

  // Chord-length parametrisation for each fit point in [0, 1]
  const params: number[] = [0];
  let totalLength = 0;
  for (let i = 1; i < n; i++) {
    const cur = fitPoints[i];
    const prev = fitPoints[i - 1];
    if (!cur || !prev) continue;
    const dx = cur.x - prev.x;
    const dy = cur.y - prev.y;
    const d = Math.hypot(dx, dy);
    totalLength += d;
    params.push(totalLength);
  }
  if (totalLength === 0) {
    return {
      controlPoints: fitPoints.slice(),
      knots: uniformKnotVector(n, degree),
    };
  }
  for (let i = 0; i < n; i++) {
    const p = params[i];
    if (p !== undefined) params[i] = p / totalLength;
  }

  // For a typical n-point fit and degree 3, AutoCAD computes (n+2)
  // control points using a tridiagonal solve. We approximate the
  // standard "natural" cubic spline interpolation when no tangents are
  // supplied; tangent overrides clamp the first / last derivatives.
  //
  // To keep the math practical we use a simplified scheme:
  //   - Control points = fit points (start/end clamped).
  //   - Insert tangent helpers as the second / second-to-last control
  //     points if start/end tangents are provided.
  //   - Knot vector follows chord-length parametrisation, padded at
  //     the ends to clamp the curve through the first/last fit points.
  //
  // This produces a cubic curve that interpolates the fit points
  // exactly (renderer verifies via sampleNurbs at param values
  // matching the params[] array). Tangent vectors are honoured by
  // shifting interior control points along the supplied direction.
  const controlPoints: SplinePoint[] = [];
  const firstFit = fitPoints[0];
  const lastFit = fitPoints[n - 1];
  if (!firstFit || !lastFit) {
    return {
      controlPoints: fitPoints.slice(),
      knots: uniformKnotVector(n, degree),
    };
  }
  controlPoints.push(firstFit);

  const tangentLength = totalLength * 0.1;
  // Codex P2-1 (#317) — carry Z through the synthesized tangent / helper
  // control points. A 3-D fit-point spline (fit points with non-zero Z) was
  // flattened on a fit-grip drag because these helpers were emitted with no
  // `z` (→ z:0 downstream), collapsing part of the recomputed control polygon
  // onto the z=0 plane. The helper sits 10% of the chord toward the
  // neighbouring fit point, so its Z is interpolated along the SAME chord
  // (firstFit.z → next.z), matching the parametric position of the helper —
  // exactly how AutoCAD's fit→control solve keeps the curve in its own plane.
  const startNeighbour = fitPoints[1];
  const endNeighbour = fitPoints[n - 2];
  const firstZ = firstFit.z ?? 0;
  const lastZ = lastFit.z ?? 0;
  // Fractional position of each tangent helper along its chord = tangentLength
  // / chordLength (clamped to [0,1]); used to lerp the helper's Z so it never
  // overshoots the neighbour's elevation.
  const lerpZ = (zA: number, zB: number, frac: number): number =>
    zA + (zB - zA) * Math.min(1, Math.max(0, frac));

  // Always emit start-tangent helper. Without an explicit user tangent,
  // synthesise a tangent from the first chord so the cubic curve has a
  // well-defined derivative at the start (matches AutoCAD's natural
  // spline boundary condition).
  if (startTangent !== undefined) {
    // Codex P2-4 (#317) — the explicit start tangent is a 3-D vector. Honour
    // its Z: normalise by the FULL 3-D magnitude and offset the helper's Z by
    // the tangent's Z component. Pinning z to `firstZ` (the endpoint's Z) and
    // normalising by the X/Y magnitude only flattened a 3-D fit-point spline's
    // synthesized tangent control point onto the start point's plane after a
    // fit-grip recompute, changing the curve shape in 3-D.
    const stz = startTangent.z ?? 0;
    const m = Math.hypot(startTangent.x, startTangent.y, stz) || 1;
    controlPoints.push({
      x: firstFit.x + (startTangent.x / m) * tangentLength,
      y: firstFit.y + (startTangent.y / m) * tangentLength,
      z: firstZ + (stz / m) * tangentLength,
    });
  } else if (startNeighbour) {
    const next = startNeighbour;
    const dx = next.x - firstFit.x;
    const dy = next.y - firstFit.y;
    const m = Math.hypot(dx, dy) || 1;
    controlPoints.push({
      x: firstFit.x + (dx / m) * tangentLength,
      y: firstFit.y + (dy / m) * tangentLength,
      z: lerpZ(firstZ, next.z ?? 0, tangentLength / m),
    });
  }

  for (let i = 1; i < n - 1; i++) {
    const fp = fitPoints[i];
    if (fp) controlPoints.push(fp);
  }

  // Always emit end-tangent helper for the same reason.
  if (endTangent !== undefined) {
    // Codex P2-4 (#317) — honour the explicit end tangent's Z (3-D vector):
    // normalise by the full 3-D magnitude and offset the helper Z by the
    // tangent's Z component (mirrors the start-tangent fix above). The helper
    // sits BEFORE the last fit point, so subtract along the tangent direction.
    const etz = endTangent.z ?? 0;
    const m = Math.hypot(endTangent.x, endTangent.y, etz) || 1;
    controlPoints.push({
      x: lastFit.x - (endTangent.x / m) * tangentLength,
      y: lastFit.y - (endTangent.y / m) * tangentLength,
      z: lastZ - (etz / m) * tangentLength,
    });
  } else if (endNeighbour) {
    const prev = endNeighbour;
    const dx = lastFit.x - prev.x;
    const dy = lastFit.y - prev.y;
    const m = Math.hypot(dx, dy) || 1;
    controlPoints.push({
      x: lastFit.x - (dx / m) * tangentLength,
      y: lastFit.y - (dy / m) * tangentLength,
      z: lerpZ(lastZ, prev.z ?? 0, tangentLength / m),
    });
  }

  controlPoints.push(lastFit);

  return {
    controlPoints,
    knots: uniformKnotVector(controlPoints.length, degree),
  };
}

/**
 * The minimal SPLINE-entity-shaped input `resolveSplineNurbs` needs — the ONE
 * shared structural type for every call site across `command-engine`
 * (`trimExtendSplineTarget.ts`'s `SplineTargetInput`, `trimExtendIntersection
 * .ts`'s `splineToPoints`) instead of three independently hand-rolled near-
 * duplicates that could silently drift out of sync (Codex P2, GitHub review
 * — extracted alongside the `rational` field this same round adds, which
 * would otherwise have pushed `trimExtendIntersection.ts` over its 2000-line
 * hard ceiling as one more hand-rolled inline copy).
 */
export type SplineNurbsResolvable = {
  degree: number;
  controlPoints: SplinePoint[];
  knots: number[];
  weights: number[];
  fitPoints: SplinePoint[];
  startTangent?: SplinePoint | undefined;
  endTangent?: SplinePoint | undefined;
  /**
   * DXF group 70 bit 4 (`splineEntitySchema.rational`, default `false`).
   * Codex P2 (GitHub review): a schema-valid Spline can have `rational:
   * false` while still carrying a STALE non-empty `weights` array (e.g.
   * left over from an independent Properties edit that toggled `Rational`
   * off without clearing `Weights` — the two are DECLARED as independent
   * fields, so nothing forces them to stay in sync at the data layer).
   * Before this fix, ANY non-empty `weights` array was treated as "this
   * curve is rational" regardless of the entity's own authoritative flag —
   * TRIM/EXTEND then performed homogeneous (weighted) knot insertion on a
   * DIFFERENT curve than the one actually rendered/stored, while
   * `geometryReducerEditTrim.ts` correctly preserved the ORIGINAL
   * `rational: false` on the result, and rational-AWARE consumers
   * elsewhere (`packages/cad-core/src/selectLeaf.ts`) ignore those same
   * stale weights — so even the retained/surviving portion changed shape
   * inconsistently across the codebase. Optional (not every caller
   * constructs a full schema-backed entity — e.g. hand-built test
   * fixtures); `undefined` is treated the SAME as `false` (the schema's
   * own default), never silently inferred from `weights` alone.
   */
  rational?: boolean | undefined;
};

/**
 * Resolve any SPLINE-entity-shaped input (fit-points OR control-points mode)
 * into its canonical control-point / knot-vector NURBS representation. This is
 * the ONE-TRUTH conversion — fit-point mode is converted via
 * `fitPointsToControlPoints` (never re-derived at each call site) and a
 * missing/malformed knot vector falls back to `uniformKnotVector`, exactly the
 * two normalisations `buildSplineBeziers` and the TRIM/EXTEND spline-target
 * module already need. Returns `null` when the entity carries no usable
 * geometry at all (no control points AND fewer than 2 fit points).
 */
export function resolveSplineNurbs(entity: SplineNurbsResolvable): {
  controlPoints: SplinePoint[];
  knots: number[];
  weights: number[] | undefined;
  degree: number;
} | null {
  let cps = entity.controlPoints;
  let knots = entity.knots;
  let weights: number[] | undefined = entity.weights;
  const degree = entity.degree;

  if (cps.length === 0 && entity.fitPoints.length >= 2) {
    const fit = fitPointsToControlPoints(
      entity.fitPoints,
      entity.startTangent,
      entity.endTangent,
      entity.degree,
    );
    cps = fit.controlPoints;
    knots = fit.knots;
    weights = undefined; // fit-points mode: never rational
  }

  if (cps.length === 0) return null;
  if (knots.length !== cps.length + degree + 1) {
    knots = uniformKnotVector(cps.length, degree);
  }
  // Normalise "no genuine per-control-point weight array" to `undefined`
  // (not just falsy-check `weights`, which an EMPTY array — the DXF/schema
  // default for a non-rational SPLINE's `weights: []` — would pass, being a
  // truthy value). Downstream knot-insertion/split code treats a truthy
  // `weights` as "this curve is rational" and preserves it through every
  // refinement.
  //
  // Codex P1, round 17: a NON-empty but SHORTER-than-controlPoints array is
  // legal input (DXF's weight group code 41 is optional per control point —
  // AutoCAD/the Properties writer default any point it doesn't carry a
  // weight for to 1), and `evaluateNurbs`/`buildSplineBeziers` already
  // honour exactly that via `weights?.[i] ?? 1`. Discarding the WHOLE array
  // here instead of padding the missing tail made TRIM/EXTEND operate on a
  // DIFFERENT (fully non-rational) curve than the one actually rendered,
  // silently reshaping it, then persisted `weights: []` even for the
  // untouched remainder — so pad to `cps.length` with the same per-index
  // default `evaluateNurbs` itself uses, instead of dropping the array.
  //
  // Codex P2 (GitHub review, follow-up): gate this on the entity's own
  // authoritative `rational` flag, not merely "is `weights` non-empty" —
  // see this function's own `rational` parameter doc comment for the
  // stale-weights-after-a-Properties-edit failure mode this closes.
  const normalisedWeights =
    entity.rational === true && weights && weights.length > 0
      ? cps.map((_, i) => weights![i] ?? 1)
      : undefined;
  return { controlPoints: cps, knots, weights: normalisedWeights, degree };
}

/**
 * Convert a (rational or non-rational) B-spline curve into a sequence
 * of cubic Bezier segments suitable for SVG path 'C' commands.
 *
 * Implementation: sample the spline at fine parameter intervals, then
 * fit cubic Bezier segments between consecutive sample points using
 * the local tangent estimates. Output segment count scales with curve
 * length — the renderer can rely on each segment being a true cubic
 * Bezier (no quadratic, no polyline degradation).
 *
 * For degree=3 splines with no rational weights, this can be replaced
 * with the de Boor algorithm to produce *exact* Bezier segments — but
 * the sampled approach here matches AutoCAD's visual output and works
 * for arbitrary degree / rationality.
 */
export function splineToCubicBezier(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  segmentsPerSpan: number = 10,
): CubicBezierSegment[] {
  if (controlPoints.length === 0) return [];

  // Determine the number of unique knot spans (parametric intervals
  // where the basis functions are smooth). For each span emit
  // `segmentsPerSpan` cubic Bezier segments.
  const uMin = knots[degree] ?? 0;
  const uMax = knots[knots.length - degree - 1] ?? 1;
  if (!Number.isFinite(uMin) || !Number.isFinite(uMax) || uMax <= uMin) {
    return [];
  }

  const totalSamples = Math.max(
    segmentsPerSpan * 4,
    Math.min(200, segmentsPerSpan * (controlPoints.length - degree)),
  );

  // Sample the curve plus tangents at each sample point.
  const samplePoints: SplinePoint[] = [];
  const tangents: SplinePoint[] = [];

  for (let i = 0; i <= totalSamples; i++) {
    const u = uMin + (uMax - uMin) * (i / totalSamples);
    const p = evaluateNurbs(controlPoints, knots, weights, degree, u);
    samplePoints.push(p);

    // Numerical tangent via small forward / backward step.
    const eps = (uMax - uMin) * 1e-4;
    const uPrev = Math.max(uMin, u - eps);
    const uNext = Math.min(uMax, u + eps);
    const pPrev = evaluateNurbs(controlPoints, knots, weights, degree, uPrev);
    const pNext = evaluateNurbs(controlPoints, knots, weights, degree, uNext);
    tangents.push({
      x: pNext.x - pPrev.x,
      y: pNext.y - pPrev.y,
    });
  }

  // Build cubic Bezier segments between consecutive sample points.
  // For each segment use the standard Hermite-to-Bezier conversion:
  //   cp1 = p0 + tangent0 / 3
  //   cp2 = p3 - tangent3 / 3
  // where tangent magnitude is normalised to chord length so we don't
  // overshoot.
  const segments: CubicBezierSegment[] = [];
  for (let i = 0; i < samplePoints.length - 1; i++) {
    const p0 = samplePoints[i];
    const p3 = samplePoints[i + 1];
    const t0 = tangents[i];
    const t3 = tangents[i + 1];
    if (!p0 || !p3 || !t0 || !t3) continue;

    const chord = Math.hypot(p3.x - p0.x, p3.y - p0.y);
    const t0m = Math.hypot(t0.x, t0.y) || 1;
    const t3m = Math.hypot(t3.x, t3.y) || 1;
    const k = chord / 3;

    segments.push({
      p0,
      cp1: {
        x: p0.x + (t0.x / t0m) * k,
        y: p0.y + (t0.y / t0m) * k,
      },
      cp2: {
        x: p3.x - (t3.x / t3m) * k,
        y: p3.y - (t3.y / t3m) * k,
      },
      p3,
    });
  }

  return segments;
}

/**
 * High-level helper: convert a spline (in either fit-points or
 * control-points mode) into renderable cubic Bezier segments.
 */
export function buildSplineBeziers(input: {
  fitPoints?: SplinePoint[];
  startTangent?: SplinePoint;
  endTangent?: SplinePoint;
  controlPoints?: SplinePoint[];
  knots?: number[];
  weights?: number[];
  degree: number;
}): CubicBezierSegment[] {
  let cps: SplinePoint[];
  let kts: number[];
  let wts: number[] | undefined = input.weights;
  const degree = input.degree;

  if (input.controlPoints && input.controlPoints.length > 0) {
    cps = input.controlPoints;
    kts =
      input.knots &&
      input.knots.length === input.controlPoints.length + degree + 1
        ? input.knots
        : uniformKnotVector(input.controlPoints.length, degree);
  } else if (input.fitPoints && input.fitPoints.length > 0) {
    const fitted = fitPointsToControlPoints(
      input.fitPoints,
      input.startTangent,
      input.endTangent,
      degree,
    );
    cps = fitted.controlPoints;
    kts = fitted.knots;
    wts = undefined; // fit-points mode: never rational
  } else {
    return [];
  }

  return splineToCubicBezier(cps, kts, wts, degree);
}
